import React, { useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {
  Box,
  IconButton,
  Tooltip as MuiTooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { useData } from '../context/DataContext';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend, ChartDataLabels);

const CostosAgrupadosBar = ({ selectedIndustries = [], onBarClick = () => {} }) => {
  const [infoOpen, setInfoOpen] = useState(false);
  const { getThreeCosts, dataReady } = useData();

  // Get data from Excel (Q24)
  const excelData = dataReady ? getThreeCosts() : {
    labels: [],
    transport2022: [], distribution2022: [], logistics2022: [],
    transport2023: [], distribution2023: [], logistics2023: [],
    transport2024: [], distribution2024: [], logistics2024: []
  };

  // Use Excel data or fallback
  const allLabels = excelData.labels.length > 0
    ? excelData.labels
    : ['Servicios Logísticos', 'Automotriz', 'Manufactura', 'Alimentos', 'Construcción', 'Sector salud/ Farmacéuticas', 'Retail'];

  const labels = selectedIndustries.length > 0 ? selectedIndustries : allLabels;

  const getValuesForLabels = (allValues) => labels.map((label) => {
    const index = allLabels.indexOf(label);
    return index !== -1 ? (allValues[index] || 0) : 0;
  });

  // Build chart data for each year with 3 cost types
  // Using app's standard color scheme: #A2AEBB (2022), #476A8E (2023), #1F3D58 (2024)
  const chartData2022 = {
    labels: labels,
    datasets: [
      {
        label: 'Costo de transporte',
        data: getValuesForLabels(excelData.transport2022),
        backgroundColor: '#C5D3E0',
      },
      {
        label: 'Costo de distribución',
        data: getValuesForLabels(excelData.distribution2022),
        backgroundColor: '#A2AEBB',
      },
      {
        label: 'Costo logístico total',
        data: getValuesForLabels(excelData.logistics2022),
        backgroundColor: '#7A8A9A',
      },
    ],
  };

  const chartData2023 = {
    labels: labels,
    datasets: [
      {
        label: 'Costo de transporte',
        data: getValuesForLabels(excelData.transport2023),
        backgroundColor: '#6B8DB5',
      },
      {
        label: 'Costo de distribución',
        data: getValuesForLabels(excelData.distribution2023),
        backgroundColor: '#476A8E',
      },
      {
        label: 'Costo logístico total',
        data: getValuesForLabels(excelData.logistics2023),
        backgroundColor: '#2E4A66',
      },
    ],
  };

  const chartData2024 = {
    labels: labels,
    datasets: [
      {
        label: 'Costo de transporte',
        data: getValuesForLabels(excelData.transport2024),
        backgroundColor: '#3A5F7D',
      },
      {
        label: 'Costo de distribución',
        data: getValuesForLabels(excelData.distribution2024),
        backgroundColor: '#1F3D58',
      },
      {
        label: 'Costo logístico total',
        data: getValuesForLabels(excelData.logistics2024),
        backgroundColor: '#0F2030',
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
      },
      tooltip: {
        callbacks: {
          label: (context) => `${context.dataset.label}: ${context.raw}%`,
        },
      },
      datalabels: {
        color: '#000',
        font: {
          weight: 'bold',
          size: 9,
        },
        anchor: 'end',
        align: 'top',
        formatter: (value) => `${value}%`,
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Industria',
        },
      },
      y: {
        beginAtZero: true,
        max: 35,
        ticks: {
          stepSize: 5,
          callback: (value) => `${value}%`,
        },
        title: {
          display: true,
          text: 'Porcentaje (%)',
        },
      },
    },
  };

  const infoContent = {
    formula: 'Costo (%) = (Costo / Ventas totales) × 100',
    description: 'Muestra los costos de transporte, distribución y logísticos asociados a las ventas netas segmentadas por industria.',
  };

  return (
    <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '20px' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <MuiTooltip title="Información de la gráfica">
          <IconButton onClick={() => setInfoOpen(true)}>
            <InfoIcon />
          </IconButton>
        </MuiTooltip>
      </Box>

      <Box mb={4}>
        <Typography variant="h6" align="center" gutterBottom>
          2023
        </Typography>
        <div style={{ height: '280px' }}>
          <Bar data={chartData2022} options={options} />
        </div>
      </Box>

      <Box mb={4}>
        <Typography variant="h6" align="center" gutterBottom>
          2024
        </Typography>
        <div style={{ height: '280px' }}>
          <Bar data={chartData2023} options={options} />
        </div>
      </Box>

      <Box>
        <Typography variant="h6" align="center" gutterBottom>
          2025
        </Typography>
        <div style={{ height: '280px' }}>
          <Bar data={chartData2024} options={options} />
        </div>
      </Box>

      <Dialog open={infoOpen} onClose={() => setInfoOpen(false)}>
        <DialogTitle>Información de la Gráfica</DialogTitle>
        <DialogContent>
          <p><strong>Fórmula:</strong> {infoContent.formula}</p>
          <p><strong>Descripción:</strong> {infoContent.description}</p>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoOpen(false)} color="primary">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CostosAgrupadosBar;
